import React, { useState } from "react";
import { useForm } from "react-hook-form";
import Card from "../Card/Card";
import { Col, ControlLabel, Form, FormGroup, HelpBlock, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import Button from "../CustomButton/CustomButton";
import BeatLoader from "react-spinners/BeatLoader";
import SettingsInput from "../Settings/SettingsInput";
import WaitlistSettingsHour from "../Settings/WaitlistSettingsHour";
import { reservationPossibleValues, week } from "../../constants/time";
import { filterActiveServices, sortServices } from "../../helpers/appointmentService.helper";
import {
  buildResourceSchedulePayload,
  hasCustomSchedule,
  normalizeWeeklySchedule
} from "../../helpers/effectiveHours.helper";
import SpecificDateCalendar from "../SpecificDates/SpecificDateCalendar";
import "./AddResource.scss";

const AddResource = ({
  showResourceModal,
  closeModal,
  resource,
  updateResource,
  loading,
  services = [],
  businessHours = [],
  isAppointmentResource = false,
  appointmentResourceLabelSingular = "",
  business
}) => {
  const { register, handleSubmit, errors } = useForm();

  const [form, setForm] = useState({
    name: resource?.name || "",
    maxReservations: resource?.maxReservations * 1 || 1,
    maxPartySize: resource?.maxPartySize || 1,
    minDuration: (resource && resource.minDuration) || 15,
    maxDuration: (resource && resource.maxDuration) || 15,
    addNotificationText: resource && !!resource.notificationText,
    notificationText: (resource && resource.notificationText) || "",
    // Guest-facing overrides. `name` stays the internal label used in the admin views;
    // these two are what the booking flow shows (plan §3.2).
    displayName: resource?.displayName || "",
    bio: resource?.bio || ""
  });

  const [serviceIds, setServiceIds] = useState(resource?.serviceIds || []);

  // A resource with no `schedule` inherits the business booking hours, so a fresh
  // custom schedule starts as a copy of those rather than something arbitrary.
  const [useCustomSchedule, setUseCustomSchedule] = useState(hasCustomSchedule(resource));
  const [schedule, setSchedule] = useState(normalizeWeeklySchedule(resource?.schedule || businessHours));
  // WaitlistSettingsHour keeps internal state that never re-syncs to its `value`
  // prop, so reseeding the rows has to remount them.
  const [scheduleKey, setScheduleKey] = useState(0);
  const [showSpecificHours, setShowSpecificHours] = useState(false);

  // Only services that are still bookable can be assigned. A service that was
  // switched off stays on the resource until it is unassigned here.
  const serviceOptions = sortServices(filterActiveServices(services)).map(service => ({
    value: service.id,
    label: service.name
  }));

  const selectedServiceOptions = serviceIds
    .map(id => serviceOptions.find(option => option.value === id))
    .filter(Boolean);

  const onSubmit = data => {
    if (!form.addNotificationText) {
      delete data.notificationText;
    }
    if (!isAppointmentResource) {
      updateResource(data);
      return;
    }
    data.serviceIds = serviceIds;
    // Party size has no meaning for an appointment resource and its input is not
    // rendered, so carry the stored value through rather than writing undefined.
    data.maxPartySize = resource?.maxPartySize || 1;

    const { data: scheduleData, clearedFields } = buildResourceSchedulePayload(useCustomSchedule, schedule);
    Object.assign(data, scheduleData);

    // Blank optional fields are removed rather than stored as "" — under `{ merge: true }`
    // an empty string would read back as a real value and render as an empty line.
    const cleared = [...clearedFields];
    ["displayName", "bio"].forEach(field => {
      const value = (data[field] || "").trim();
      if (value) {
        data[field] = value;
      } else {
        delete data[field];
        cleared.push(field);
      }
    });

    updateResource(data, cleared);
  };

  const toggleChecked = event => {
    setForm({ ...form, [event.target.name]: event.target.checked });
  };

  const toggleCustomSchedule = event => {
    const checked = event.target.checked;
    setUseCustomSchedule(checked);
    if (checked && !hasCustomSchedule(resource)) {
      // Seed from the business hours the resource was inheriting until now.
      setSchedule(normalizeWeeklySchedule(businessHours));
      setScheduleKey(key => key + 1);
    }
  };

  // Mirrors ReservationSettings' booking-hours handler, but clones the row instead
  // of mutating it in place.
  const onScheduleChange = (day, values) => {
    const index = week.indexOf(day);
    if (index < 0) {
      return;
    }
    setSchedule(current => {
      const next = [...current];
      const row = { ...next[index] };
      if (values.begin !== undefined) {
        row.begin = values.begin;
      }
      if (values.end !== undefined) {
        row.end = values.end;
      }
      if (values.closed !== undefined) {
        row.closed = values.closed;
      }
      next[index] = row;
      return next;
    });
  };

  const handleTextareaChange = key => event => {
    let value;
    if (typeof event === "string") {
      value = event;
    } else {
      value = event.target.value;
    }
    setForm({ ...form, [key]: value });
  };

  return (
    <>
      <Modal
        enforceFocus={false}
        dialogClassName={isAppointmentResource ? "add-resource-modal" : "add-guest-modal"}
        show={showResourceModal}
        onHide={closeModal}
      >
        <Card
          content={
            <Form noValidate className="edit-user" onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col className="edit-user__title" md={12}>
                  {`${resource?.id ? "Edit" : "Add"} ${appointmentResourceLabelSingular || "Resource"}`}
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <FormGroup validationState={(!!errors.name && "error") || null}>
                    <ControlLabel className="edit-user-label">NAME</ControlLabel>
                    <input
                      name="name"
                      className="form-control"
                      type="text"
                      disabled={loading}
                      placeholder="Enter name"
                      defaultValue={form.name}
                      ref={register({ required: true })}
                    />
                    {errors.name && errors.name.type === "required" && <HelpBlock>Name is required</HelpBlock>}
                  </FormGroup>
                </Col>
              </Row>
              {isAppointmentResource ? (
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <ControlLabel className="edit-user-label">SERVICES</ControlLabel>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        options={serviceOptions}
                        placeholder={`Select the services this ${appointmentResourceLabelSingular || "resource"} performs`}
                        value={selectedServiceOptions}
                        onChange={selected => setServiceIds((selected || []).map(option => option.value))}
                        isDisabled={loading}
                        name="serviceIds"
                        isMulti
                        isClearable={false}
                      />
                      <HelpBlock style={{ color: "#626262" }}>
                        {serviceOptions.length
                          ? "A service can only be booked with a resource that performs it."
                          : "Add a bookable service first — then it can be assigned here."}
                      </HelpBlock>
                    </FormGroup>
                    <FormGroup>
                      <ControlLabel className="edit-user-label">DISPLAY NAME (OPTIONAL)</ControlLabel>
                      <input
                        name="displayName"
                        className="form-control"
                        type="text"
                        disabled={loading}
                        placeholder="Shown to guests instead of the name above"
                        defaultValue={form.displayName}
                        ref={register({ maxLength: 60 })}
                      />
                      {errors.displayName && <HelpBlock>Display name should be less than 60 characters</HelpBlock>}
                    </FormGroup>
                    <FormGroup>
                      <ControlLabel className="edit-user-label">DESCRIPTION (OPTIONAL)</ControlLabel>
                      <textarea
                        name="bio"
                        className="form-control"
                        disabled={loading}
                        maxLength={300}
                        style={{ resize: "none", height: 70 }}
                        placeholder="Shown under the name when guests choose — e.g. 20 years experience, or Ground floor, wheelchair accessible"
                        defaultValue={form.bio}
                        ref={register({ maxLength: 300 })}
                      />
                    </FormGroup>
                    <FormGroup className="add-resource-modal__schedule">
                      <div className="add-resource-modal__schedule-header">
                        <ControlLabel className="edit-user-label">CUSTOM WEEKLY HOURS</ControlLabel>
                        <Button
                          onClick={() => setShowSpecificHours(true)}
                          bsStyle="info"
                          disabled={!resource?.id || loading}
                          className="add-resource-modal__specific-hours-button"
                          type="button"
                        >
                          Set Specific Hours
                        </Button>
                      </div>
                      <SettingsInput
                        form={form}
                        name={"useCustomSchedule"}
                        type={"switch"}
                        enabled={!loading}
                        style={{ marginBottom: 0 }}
                        value={useCustomSchedule}
                        register={null}
                        onChangeValue={toggleCustomSchedule}
                      />
                      <HelpBlock style={{ color: "#626262" }}>
                        {useCustomSchedule
                          ? "These hours drive the appointment booking flow. The current booking page still uses the business booking hours."
                          : "This resource is bookable during the business booking hours."}
                      </HelpBlock>
                      <HelpBlock style={{ color: "#626262" }}>
                        {resource?.id
                          ? "Override these hours for specific dates such as holidays."
                          : `Save this ${appointmentResourceLabelSingular || "resource"} first to set specific hours for it.`}
                      </HelpBlock>
                      {useCustomSchedule &&
                        schedule.map((hour, index) => (
                          <WaitlistSettingsHour
                            key={`${scheduleKey}-${index}`}
                            time={hour}
                            value={!hour.closed}
                            disabled={loading}
                            day={week[index]}
                            onValidChange={onScheduleChange}
                          />
                        ))}
                    </FormGroup>
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col md={12}>
                    <FormGroup validationState={(!!errors.maxPartySize && "error") || null}>
                      <ControlLabel className="edit-user-label">PARTY SIZE ACCOMMODATION</ControlLabel>
                      <input
                        name="maxPartySize"
                        className="form-control"
                        type="number"
                        disabled={loading}
                        placeholder="Max party size"
                        defaultValue={form.maxPartySize}
                        ref={register({ required: true, max: 200, min: 1 })}
                      />
                      {errors.maxPartySize && errors.maxPartySize.type === "required" && (
                        <HelpBlock>This field is required</HelpBlock>
                      )}
                      {errors.maxPartySize && errors.maxPartySize.type === "max" && (
                        <HelpBlock>Party size should be less than 200</HelpBlock>
                      )}
                      {errors.maxPartySize && errors.maxPartySize.type === "min" && (
                        <HelpBlock>Min party size should be at least 1</HelpBlock>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              )}
              <Row>
                <Col md={12}>
                  <FormGroup validationState={(!!errors.minDuration && "error") || null}>
                    <ControlLabel className="edit-user-label">DEFAULT DURATION</ControlLabel>
                    <select
                      className="form-control"
                      placeholder="Default duration"
                      disabled={loading}
                      name="minDuration"
                      ref={register({ required: true })}
                      defaultValue={form.minDuration || ""}
                    >
                      {reservationPossibleValues.map((time, index) => (
                        <option key={index} value={time}>
                          {time}
                        </option>
                      ))}
                      <option key="allday" value="-1">
                        All day time slot
                      </option>
                    </select>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <FormGroup style={{ margin: 0 }}>
                    <ControlLabel style={{ float: "left", marginTop: 17, fontSize: 14 }}>
                      CUSTOM NOTIFCATION TEXT
                    </ControlLabel>
                    <SettingsInput
                      form={form}
                      name={"addNotificationText"}
                      type={"switch"}
                      enabled={true}
                      style={{ marginBottom: 0 }}
                      value={form.addNotificationText}
                      register={null}
                      onChangeValue={toggleChecked}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <ControlLabel
                      style={{ marginTop: 17, fontSize: 14, color: !form.addNotificationText && "#CAC9C9" }}
                    >
                      NOTIFICATION TEXT
                    </ControlLabel>
                    <SettingsInput
                      form={form}
                      name={"notificationText"}
                      type={"textarea"}
                      errors={errors}
                      hideLabel={true}
                      enabled={form.addNotificationText}
                      value={form.notificationText}
                      link={false}
                      register={register}
                      onChangeValue={handleTextareaChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <FormGroup validationState={(!!errors.maxReservations && "error") || null}>
                    <ControlLabel className="edit-user-label">SIMULTANEOUS BOOKINGS</ControlLabel>
                    <input
                      name="maxReservations"
                      className="form-control"
                      type="number"
                      disabled={loading}
                      placeholder="Simulataneous Bookings"
                      defaultValue={form.maxReservations}
                      ref={register({ required: true, max: 200, min: 1 })}
                    />
                    {errors.maxReservations && errors.maxReservations.type === "required" && (
                      <HelpBlock>This field is required</HelpBlock>
                    )}
                    {errors.maxReservations && errors.maxReservations.type === "max" && (
                      <HelpBlock>Simultaneous bookings should be less than 200</HelpBlock>
                    )}
                    {errors.maxReservations && errors.maxReservations.type === "min" && (
                      <HelpBlock>Min simultaneous bookings should be at least 1</HelpBlock>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="edit-user__actions">
                    <Button
                      onClick={closeModal}
                      className="add-reservation__cancel"
                      bsStyle="info"
                      pullRight
                      disabled={loading}
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button disabled={loading} bsStyle="info" pullRight fill type="submit">
                      {loading ? (
                        <BeatLoader margin={2.5} size={10} color={"#fff"} />
                      ) : (
                        <span>{resource?.id ? "UPDATE" : "ADD"}</span>
                      )}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          }
        />
      </Modal>
      {showSpecificHours && resource?.id && (
        <SpecificDateCalendar
          showSpecificDateModal={showSpecificHours}
          business={business}
          resourceId={resource.id}
          resourceName={form.name}
          closeModal={() => setShowSpecificHours(false)}
        />
      )}
    </>
  );
};

export default AddResource;
